// src/components/editor/modals/BookPreviewModal.jsx
//
// Read-only "Book Preview" overlay — presents the manuscript as a real book you
// can flip through: a 3D-ish page-turn (StPageFlip via react-pageflip) with soft
// shadows, click-a-corner / drag / arrow-key turning, centered on a dark stage.
//
// The content is the CLEANED editor HTML (getCleanHTML already stripped
// decorations / highlights). It is rendered with dangerouslySetInnerHTML and is
// NOT contentEditable, so this preview is strictly read-only — nothing here can
// change the manuscript.
//
// Each .rv-page sheet is the book's own trim size at 96dpi — 816×1056 for Letter,
// 576×864 for Trade 6×9, and so on — taken from `pageSizePx` so the preview and
// the editor cannot disagree about how big a page is. A full open spread is wider
// than most screens, so we compute a fit-scale from the stage size and render
// each native sheet transform-scaled into a flip page of the matching pixel size
// (the flip geometry stays correct because the flip page's own box is real px).
//
// The cover is drawn into the SAME box as the sheets, so a cover made at the
// book's trim size fills its page exactly, with no letterboxing. When the linked
// cover was made at a different size the gap is real and is called out in the
// header rather than silently absorbed by `object-fit`.
import React, { useEffect, useMemo, useState, useRef, useLayoutEffect, useCallback } from 'react';
import { IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HTMLFlipBook from 'react-pageflip';

import useLinkedCoverImage from '../../../hooks/useLinkedCoverImage';
import { splitPages } from '../../../utils/manuscriptPages';
import { DEFAULT_BOOK_SIZE, bookSize, dimensionsLabel, pageSizePx } from '../../../config/bookSizes';

const FIT_MARGIN = 120; // room for arrows + breathing space around the open book
// One value for the page turn and for the re-centering that rides along with it;
// the two drifting apart is what makes a turn look like two separate movements.
const FLIP_MS = 700;
// Arriving at a cover is the other half of the motion, and it cannot ride along
// with the turn: the destination is only known once the turn has landed, so this
// runs after it. Short and eased, so it reads as the cover settling into place
// rather than as the book drifting sideways on its own.
const SETTLE_MS = 260;

export default function BookPreviewModal({
  open,
  onClose,
  html = '',
  title = 'Book Preview',
  draft = null,
  trimSize = DEFAULT_BOOK_SIZE,
}) {
  // The page box, from the size this book is actually printed at.
  //
  // These were hardcoded to 816 × 1056 under a comment claiming they "must
  // match --rv-page-width" — which stopped being true when trim sizes landed.
  // `EditorPage.onPageSetupChange` drives those CSS vars off the chosen size, so
  // a 6 × 9 manuscript was being written on 6 × 9 sheets and previewed on Letter
  // ones. Same source of truth as the editor now: `pageSizePx`.
  const { width: PAGE_W, height: PAGE_H } = useMemo(() => pageSizePx(trimSize), [trimSize]);
  // The real cover, when this manuscript has one. Null — no link, no permission,
  // no saved image, request failed — falls through to the drawn placeholder, so
  // the preview never regresses to a blank page. Gated on `open` so the fetch
  // happens when the preview is opened rather than when the editor mounts: the
  // URL is presigned for an hour and an editor session outlives that easily.
  const cover = useLinkedCoverImage(draft, open);
  const coverImageUrl = cover.url;

  // A URL that resolves is not yet an image that loads: these are presigned and
  // expire, and the preview can sit open longer than the hour they last. An
  // image that fails falls back to the drawn cover rather than leaving a broken
  // icon on a dark page. Reset per URL so a later fetch gets its own chance.
  const [coverImageFailed, setCoverImageFailed] = useState(false);
  useEffect(() => { setCoverImageFailed(false); }, [coverImageUrl]);
  const showCoverImage = Boolean(coverImageUrl) && !coverImageFailed;

  // The back face is tracked separately, not derived from the front: a cover
  // saved by the autosave thumbnail has a front and no back, so the book can
  // legitimately show real art on one cover and the drawn placeholder on the
  // other. Failing them together would blank a back cover that loads fine.
  const backImageUrl = cover.backUrl;
  const [backImageFailed, setBackImageFailed] = useState(false);
  useEffect(() => { setBackImageFailed(false); }, [backImageUrl]);
  const showBackImage = Boolean(backImageUrl) && !backImageFailed;

  // A cover drawn at one trim size and an interior set to another cannot be
  // bound into one book. The preview is where that is first visible, so it is
  // where it gets said — the alternative is finding out at the printer.
  //
  // Measured from the loaded image rather than read from `bookSize`, because
  // `bookSize` is frequently unset: a cover made before the field existed, or
  // through any path that does not seed it from a quote, carries null and would
  // report "no mismatch" while visibly being the wrong shape. The image's own
  // proportions cannot be absent or stale. The metadata is still used, but only
  // to NAME the sizes once the shapes have already disagreed.
  const [coverRatio, setCoverRatio] = useState(null);
  useEffect(() => { setCoverRatio(null); }, [coverImageUrl]);

  const pageRatio = PAGE_W / PAGE_H;
  // ~1.5% of the page's aspect — tight enough to catch 6×9 on Letter (a 14%
  // gap), loose enough that a JPEG off by a pixel or two says nothing.
  const coverSizeMismatch = Boolean(
    showCoverImage && coverRatio && Math.abs(coverRatio - pageRatio) > pageRatio * 0.015,
  );

  // Split the cleaned HTML into individual page sheets.
  const pages = useMemo(() => splitPages(html), [html]);

  const bookRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Whether a turn is in progress. react-pageflip reports `onFlip` only once a
  // turn has FINISHED, and the single-cover centering offset below is derived
  // from the page number — so the book used to sit offset for the whole
  // animation and then jump sideways by half a page the instant it landed.
  // Knowing a flip has started lets the offset unwind while the page turns,
  // which is the half of the motion that was reading as broken.
  const [isFlipping, setIsFlipping] = useState(false);

  const flip = useCallback((dir) => {
    const api = bookRef.current?.pageFlip?.();
    if (!api) return;
    if (dir > 0) api.flipNext();
    else api.flipPrev();
  }, []);

  // Keyboard: Esc closes, ← / → (and PageUp/PageDown) turn pages.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') return onClose?.();
      if (e.key === 'ArrowRight' || e.key === 'PageDown') { e.preventDefault(); flip(1); }
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); flip(-1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, flip]);

  // Zoom-to-fit: measure the stage and scale a full open spread (2 pages) to fit.
  const stageRef = useRef(null);
  const [scale, setScale] = useState(0);
  useLayoutEffect(() => {
    if (!open) return;
    const recompute = () => {
      const el = stageRef.current;
      if (!el) return;
      const w = el.clientWidth - FIT_MARGIN;
      const h = el.clientHeight - FIT_MARGIN;
      const next = Math.min(w / (PAGE_W * 2), h / PAGE_H, 1);
      setScale(next > 0.05 ? next : 0.2);
    };
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [open, PAGE_W, PAGE_H]);

  // Reset to the first page whenever the preview (re)opens or content changes.
  useEffect(() => { if (open) setCurrentPage(0); }, [open, pages]);

  if (!open) return null;

  const pageW = Math.round(PAGE_W * scale);
  const pageH = Math.round(PAGE_H * scale);
  const total = pages.length;

  // A real book's leaves, in order:
  //   0            front cover            (hard, shown alone)
  //   1            inside front cover     (hard — the reverse of the same board)
  //   2 .. total+1 the manuscript
  //   [blank]      only when parity needs it, see below
  //   n-2          inside back cover      (hard)
  //   n-1          back cover             (hard, shown alone)
  //
  // The inside covers are the reverse faces of the boards, so they are hard
  // too: that is what gives each cover real thickness when it turns, instead of
  // a single sheet flapping over.
  //
  // PARITY. With `showCover` the first leaf stands alone and the rest pair up
  // (1,2) (3,4) … so the inside front cover lands opposite manuscript page 1,
  // which is what opening a book actually looks like. The same has to hold at
  // the other end, and it only does when the manuscript has an even number of
  // pages. An odd one pushes the inside back cover into the wrong half and the
  // last spread comes apart. A blank leaf absorbs it — which is also what a
  // printer does, for the same reason.
  const needsParityLeaf = total % 2 === 1;
  const blankLeaves = needsParityLeaf ? 1 : 0;

  // Index of each landmark, derived rather than written down twice.
  const insideFrontIndex = 1;
  const firstPageIndex = 2;
  const insideBackIndex = firstPageIndex + total + blankLeaves;
  const backCoverIndex = insideBackIndex + 1;

  // The book title without the modal's " — Book Preview" suffix, for the cover.
  const coverTitle = title.replace(/\s*[—-]\s*Book Preview\s*$/i, '').trim() || 'Untitled';

  const onFrontCover = currentPage === 0;
  const onBackCover = currentPage >= backCoverIndex;
  // A single hard cover is pinned to one half of the full-spread box, so it is
  // nudged back to the middle. Leaving a cover, that nudge is released as soon
  // as the turn begins: the destination is always an interior spread, which
  // needs no offset, and gliding it out under the animation is what makes the
  // turn read as one movement instead of a turn followed by a lurch.
  const restingShiftPx = onFrontCover ? -(pageW / 2) : onBackCover ? pageW / 2 : 0;
  const coverShiftPx = isFlipping ? 0 : restingShiftPx;
  // Manuscript page numbers, counted from the first real page. The covers and
  // the inside covers are leaves, not pages: a book's page count has never
  // included them, so they are named rather than numbered and they do not
  // shift the numbering of anything that follows.
  const leftPageNo = currentPage - firstPageIndex + 1;
  const rightPageNo = leftPageNo + 1;
  const inRange = (n) => n >= 1 && n <= total;

  const pageLabel = total === 0
    ? 'Empty document'
    : onFrontCover
      ? 'Front cover'
      : onBackCover
        ? 'Back cover'
        : currentPage === insideFrontIndex
          ? `Inside front cover · page 1 of ${total}`
          : currentPage + 1 >= insideBackIndex
            // The inside back cover shares a spread with the last page, the way
            // the inside front shares one with page 1 — so it is named beside
            // it rather than given a stop of its own. `currentPage + 1` because
            // a spread is addressed by its LEFT leaf.
            ? `Page ${total} of ${total} · inside back cover`
            : inRange(leftPageNo) && inRange(rightPageNo)
              ? `Pages ${leftPageNo}–${rightPageNo} of ${total}`
              : inRange(leftPageNo)
                ? `Page ${leftPageNo} of ${total}`
                : `Page ${total} of ${total}`;

  return (
    <div
      role="dialog"
      aria-label="Book preview"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        background: 'rgba(20, 18, 30, 0.9)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 16px',
          background: '#2A2640',
          color: '#fff',
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          zIndex: 2,
        }}
      >
        <MenuBookIcon sx={{ color: '#F5D76E' }} />
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        <Typography variant="caption" sx={{ opacity: 0.75, ml: 1, flex: 1 }}>
          {pageLabel} · {bookSize(trimSize).label} {dimensionsLabel(trimSize)}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.6, mr: 1 }}>
          Click a page corner or use ← → to turn
        </Typography>
        <IconButton aria-label="Close preview" onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </div>

      {/* The sizes disagree. Said here, in the preview, because this is the first
          place the two are shown together — and because the next place it would
          surface is the print run. Named sizes, not just a warning: "which one is
          wrong" is the question the author immediately has. */}
      {coverSizeMismatch && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '9px 16px',
            background: '#5A3A12',
            borderBottom: '1px solid rgba(245,215,110,0.45)',
            color: '#FFE9B0',
            flexShrink: 0,
            zIndex: 2,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 19, color: '#F5D76E', flexShrink: 0 }} />
          <Typography variant="caption" sx={{ lineHeight: 1.45 }}>
            <strong>This cover is not the same shape as the book.</strong>{' '}
            The manuscript is set to {bookSize(trimSize).label} ({dimensionsLabel(trimSize)}),
            but {cover.versionName ? `“${cover.versionName}”` : 'the linked cover'}{' '}
            {cover.trimSize
              ? `was designed at ${bookSize(cover.trimSize).label} (${dimensionsLabel(cover.trimSize)}).`
              : 'is a different proportion.'}{' '}
            A cover and an interior of different sizes cannot be bound into one book. The
            cover below is scaled to fit the page, so it is <em>not</em> how it will print.
            Open the Cover Designer and set the cover to {bookSize(trimSize).label}.
          </Typography>
        </div>
      )}

      {/* Stage: centers the book; click empty space to close. */}
      <div
        ref={stageRef}
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          userSelect: 'none',
        }}
      >
        {total === 0 && (
          <Typography sx={{ color: '#fff', opacity: 0.7 }}>Nothing to preview yet.</Typography>
        )}

        {total > 0 && scale > 0 && (
          <>
            <IconButton aria-label="Previous page" onClick={() => flip(-1)} sx={navBtnSx('left')}>
              <ChevronLeftIcon fontSize="large" />
            </IconButton>
            <IconButton aria-label="Next page" onClick={() => flip(1)} sx={navBtnSx('right')}>
              <ChevronRightIcon fontSize="large" />
            </IconButton>

            {/* Fixed frame spanning a full spread (2 pages wide) — this, not the
                flip book itself, is what the stage centers. react-pageflip
                internally lays every state out inside a full-spread-width box:
                an open spread fills it edge to edge, but a single hard cover
                is pinned to just one half (front cover -> right half, back
                cover -> left half) via absolute positioning, which flexbox
                centering can't see. coverShiftPx nudges that half-page back
                to the frame's center for the two cover states. */}
            <div className="rv-flip-frame" style={{ width: pageW * 2, height: pageH }}>
              {/* key on pageW forces a clean re-init when the fit-scale changes
                  (e.g. window resize); startPage keeps our place. */}
              <HTMLFlipBook
                key={pageW}
                ref={bookRef}
                width={pageW}
                height={pageH}
                minWidth={pageW}
                maxWidth={pageW}
                minHeight={pageH}
                maxHeight={pageH}
                size="fixed"
                showCover
                usePortrait={false}
                mobileScrollSupport
                drawShadow
                maxShadowOpacity={0.5}
                flippingTime={FLIP_MS}
                startPage={currentPage}
                className="rv-flip-book"
                style={{
                  transform: `translateX(${coverShiftPx}px)`,
                  // Two durations, because the two directions are different
                  // problems. Leaving a cover, the offset unwinds UNDER the turn
                  // and is paced to match it. Arriving at one, the correction can
                  // only happen after the turn lands, so it is quick.
                  transition: isFlipping
                    ? `transform ${FLIP_MS}ms cubic-bezier(0.33, 0, 0.2, 1)`
                    : `transform ${SETTLE_MS}ms cubic-bezier(0.2, 0, 0.1, 1)`,
                }}
                onChangeState={(e) => setIsFlipping(e.data === 'flipping' || e.data === 'user_fold')}
                onFlip={(e) => { setCurrentPage(e.data); setIsFlipping(false); }}
              >
                {/* Front cover (static, hard page) */}
                <div key="front-cover" className="rv-flip-page rv-flip-cover" data-density="hard" style={{ overflow: 'hidden' }}>
                  {showCoverImage
                    ? renderCoverImage({
                        scale,
                        url: coverImageUrl,
                        title: coverTitle,
                        onError: () => setCoverImageFailed(true),
                        onLoad: (e) => {
                          const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                          if (w && h) setCoverRatio(w / h);
                        },
                        nativeW: PAGE_W,
                        nativeH: PAGE_H,
                      })
                    : renderCover({ scale, title: coverTitle, kind: 'front', nativeW: PAGE_W, nativeH: PAGE_H })}
                </div>

                {/* Inside front cover — the reverse of the front board. Hard,
                    so the cover turns as one thick leaf rather than a sheet. */}
                <div key="inside-front" className="rv-flip-page rv-flip-endpaper" data-density="hard" style={{ overflow: 'hidden' }}>
                  {renderEndpaper({ scale, nativeW: PAGE_W, nativeH: PAGE_H, side: 'front' })}
                </div>

                {/* Manuscript pages */}
                {pages.map((pageHtml, i) => (
                  <div key={i} className="rv-flip-page" style={{ background: '#fff', overflow: 'hidden' }}>
                    <div
                      className="editor-main-content rv-flip-sheet"
                      style={{
                        width: PAGE_W,
                        height: PAGE_H,
                        // `.rv-page` sizes itself from these vars, which are
                        // declared on `:root` at Letter and only overridden
                        // inside the editor itself. Without re-declaring them
                        // here the sheet box would be the right trim size while
                        // the page drawn inside it stayed 8.5 × 11.
                        '--rv-page-width': `${PAGE_W}px`,
                        '--rv-page-height': `${PAGE_H}px`,
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                        pointerEvents: 'none',
                      }}
                      dangerouslySetInnerHTML={{ __html: pageHtml }}
                    />
                  </div>
                ))}

                {/* A blank leaf, only when the manuscript's page count is odd.
                    Without it the inside back cover lands in the wrong half of
                    the spread and the last pages come apart. Printers add one
                    for the same reason.

                    Spread from an array rather than written as `cond && <div>`:
                    a false branch leaves `false` sitting in the children, and
                    react-pageflip runs `cloneElement` over every child, which
                    throws on anything that is not an element. An even-length
                    manuscript took the whole preview down that way. */}
                {needsParityLeaf
                  ? [(
                      <div key="parity-blank" className="rv-flip-page" style={{ background: '#fff', overflow: 'hidden' }}>
                        {renderEndpaper({ scale, nativeW: PAGE_W, nativeH: PAGE_H, side: 'blank' })}
                      </div>
                    )]
                  : []}

                {/* Inside back cover — the reverse of the back board. */}
                <div key="inside-back" className="rv-flip-page rv-flip-endpaper" data-density="hard" style={{ overflow: 'hidden' }}>
                  {renderEndpaper({ scale, nativeW: PAGE_W, nativeH: PAGE_H, side: 'back' })}
                </div>

                {/* Back cover (static, hard page) */}
                <div key="back-cover" className="rv-flip-page rv-flip-cover" data-density="hard" style={{ overflow: 'hidden' }}>
                  {showBackImage
                    ? renderCoverImage({
                        scale,
                        url: backImageUrl,
                        title: coverTitle,
                        kind: 'back',
                        onError: () => setBackImageFailed(true),
                        nativeW: PAGE_W,
                        nativeH: PAGE_H,
                      })
                    : renderCover({ scale, title: coverTitle, kind: 'back', nativeW: PAGE_W, nativeH: PAGE_H })}
                </div>
              </HTMLFlipBook>

              {/* Spine divider — only meaningful once the book is open to an
                  interior spread, not while a single hard cover is showing. */}
              {!onFrontCover && !onBackCover && (
                <div className="rv-flip-divider" aria-hidden="true" />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// The inside of a cover, and the blank leaf that sometimes sits before it.
//
// Unprinted stock, not a colour sampled from the cover. The inside face of a
// printed cover is the blank reverse of the card, so this is what the finished
// book actually shows — and tinting it to match the artwork would be the
// preview inventing design the author never made. (Sampling the cover's colour
// would also be unreliable: the art is a presigned S3 image, and reading its
// pixels taints a canvas cross-origin, the same failure the cover designer
// already has to catch.)
//
// Not pure white: a flat #fff against a white manuscript page makes the leaf
// invisible and the book look like it has lost a page. A barely-warm stock with
// a gutter shadow reads as card.
function renderEndpaper({ scale, nativeW, nativeH, side }) {
  const gutterOnLeft = side === 'back';
  return (
    <div style={{ width: nativeW, height: nativeH, transform: `scale(${scale})`, transformOrigin: 'top left', pointerEvents: 'none' }}>
      <div
        style={{
          width: nativeW,
          height: nativeH,
          position: 'relative',
          background: side === 'blank' ? '#FFFFFF' : '#F6F4EF',
          boxShadow: side === 'blank'
            ? 'none'
            : `inset ${gutterOnLeft ? '' : '-'}18px 0 26px -22px rgba(26, 20, 50, 0.45)`,
        }}
      />
    </div>
  );
}

// The real cover art, on the same native-size-then-scale footing as the
// manuscript sheets and the drawn placeholder below.
//
// A cover is taller and narrower than the sheet it sits on — 6×9 against Letter
// 8.5×11 — so it cannot fill the page without losing something. It is shown
// whole (`contain`), because the part a crop would take is the top and bottom
// edge, which is exactly where a title or an author name tends to sit. The
// leftover strips are dark rather than paper-white so the result reads as a
// cover presented on a stage rather than a page that failed to load.
function renderCoverImage({ scale, url, title, onError, onLoad, nativeW, nativeH, kind = 'front' }) {
  return (
    <div style={{ width: nativeW, height: nativeH, transform: `scale(${scale})`, transformOrigin: 'top left', pointerEvents: 'none' }}>
      <div
        style={{
          width: nativeW,
          height: nativeH,
          background: '#1c1830',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <img
          src={url}
          alt={`${kind === 'back' ? 'Back' : 'Front'} cover of ${title}`}
          onError={onError}
          onLoad={onLoad}
          style={{
            // `width/height: 100%` with `contain`, NOT `max-width/max-height`:
            // the stored cover is 396×594 against an 816×1056 sheet, and max-*
            // only ever shrinks — it left the art floating at half the page
            // size. This scales it up to the page and `contain` keeps the 6×9
            // proportions intact, so the dark strips fall either side.
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
          }}
        />
      </div>
    </div>
  );
}

// Static book cover, rendered at native page size and scaled to fit (matching
// the manuscript sheets). Deep-purple "leather" with a gold frame + title.
// Still the front cover whenever there is no linked cover image to show, and
// always the back — this ticket covers the front face only.
function renderCover({ scale, title, kind, nativeW, nativeH }) {
  const isBack = kind === 'back';
  return (
    <div style={{ width: nativeW, height: nativeH, transform: `scale(${scale})`, transformOrigin: 'top left', pointerEvents: 'none' }}>
      <div
        style={{
          width: nativeW,
          height: nativeH,
          boxSizing: 'border-box',
          background: 'radial-gradient(circle at 30% 20%, #3a3358 0%, #2A2640 45%, #1c1830 100%)',
          color: '#F5D76E',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}
      >
        {/* Gold frame */}
        <div style={{ position: 'absolute', inset: 34, border: '2px solid rgba(245,215,110,0.55)', borderRadius: 4 }} />
        <div style={{ position: 'absolute', inset: 44, border: '1px solid rgba(245,215,110,0.28)', borderRadius: 2 }} />

        {isBack ? (
          <div style={{ textAlign: 'center', padding: '0 90px' }}>
            <div style={{ fontSize: 30, letterSpacing: 6, opacity: 0.85 }}>ReVisionari</div>
            <div style={{ width: 90, height: 2, background: 'rgba(245,215,110,0.6)', margin: '26px auto' }} />
            <div style={{ fontSize: 18, opacity: 0.6, fontStyle: 'italic' }}>Draft preview</div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '0 70px' }}>
            <div style={{ fontSize: 17, letterSpacing: 7, textTransform: 'uppercase', opacity: 0.7, marginBottom: 40 }}>
              A ReVisionari Manuscript
            </div>
            <div style={{ fontSize: 56, fontWeight: 700, lineHeight: 1.18, textShadow: '0 2px 10px rgba(0,0,0,0.4)' }}>
              {title}
            </div>
            <div style={{ width: 120, height: 3, background: 'rgba(245,215,110,0.7)', margin: '44px auto 0' }} />
          </div>
        )}
      </div>
    </div>
  );
}

// Floating page-turn arrow, vertically centered on the given edge.
function navBtnSx(side) {
  return {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    [side]: 12,
    zIndex: 3,
    color: '#fff',
    background: 'rgba(0,0,0,0.35)',
    '&:hover': { background: 'rgba(0,0,0,0.55)' },
  };
}
